import {
  MISSING_FORGE_CLOUD_SERVER_ERROR_CODE,
  MISSING_FORGE_CLOUD_SERVER_MESSAGE,
} from "../lib/missingForgeCloudServer";
import type { CloudRuntimeConnectProgressEvent } from "./sidebar/connectCloudRuntime";
import {
  formatTerminalFailureResidualForDisplay,
  terminalRuntimeWhyCopy,
  TERMINAL_RUNTIME_FOLLOW_UP,
  type CloudRuntimeTerminalFailureResidual,
} from "./cloudRuntimeTerminalFailureResidual";

/**
 * CMMD error codes for a runtime that no further attempt brings up: the boot
 * deadline verdict, a failed owner recycle, and the two volume failures. The
 * same set CMMD's `FORGE_RUNTIME_TERMINAL_FAILURE_CODES` refuses ensure and
 * connect on (FOR-36469). Listed here so a client that meets one renders the
 * terminal state and an exit instead of "Could not connect. Try again."
 */
export const CLOUD_RUNTIME_TERMINAL_FAILURE_CODES: ReadonlySet<string> = new Set([
  "runtime_boot_failed",
  "runtime_recycle_failed",
  "runtime_volume_missing",
  "runtime_volume_full",
]);

const CLOUD_RUNTIME_RECOVERY_CODES: ReadonlySet<string> = new Set([
  "runtime_filesystem_recovery_in_progress",
  "runtime_filesystem_unhealthy",
]);

function isCloudRuntimeTerminalFailureCode(code: string | null | undefined): boolean {
  return typeof code === "string" && CLOUD_RUNTIME_TERMINAL_FAILURE_CODES.has(code);
}

function isCloudRuntimeRecoveryCode(code: string | null | undefined): boolean {
  return typeof code === "string" && CLOUD_RUNTIME_RECOVERY_CODES.has(code);
}

export type CloudRuntimeConnectionStatus =
  | { readonly kind: "hidden" }
  | { readonly kind: "connecting"; readonly label: "Connecting..." }
  | { readonly kind: "reconnecting"; readonly label: "Reconnecting..." }
  | {
      readonly kind: "failed";
      readonly label: "Could not connect" | "Project is still recovering";
      readonly message:
        | "Try again. If this keeps happening, report the issue."
        | "No action is needed. Try again later.";
      readonly action: "retry";
    }
  | {
      readonly kind: "failed";
      readonly label:
        | "Forge cloud server needed"
        | "Could not connect"
        | "Project is still recovering";
      readonly message: string;
      readonly action: "setup-retry";
    }
  | {
      readonly kind: "failed";
      readonly label: "Workspace could not start";
      /** Per-code human why (boot deadline, recycle, volume). */
      readonly why: string;
      readonly message: typeof TERMINAL_RUNTIME_FOLLOW_UP;
      readonly action: "open-environments";
      readonly errorCode: string;
      /** Agent-readable safe fields (never CMMD raw guest error text). */
      readonly residualLine: string | null;
    };

export interface CloudRuntimeConnectionStatusInput {
  readonly events: readonly CloudRuntimeConnectProgressEvent[];
  readonly isConnecting: boolean;
  readonly error: string | null;
  /** The `code` on the CMMD refusal that produced `error`, when it carried one. */
  readonly errorCode?: string | null;
  readonly failureResidual?: CloudRuntimeTerminalFailureResidual | null;
}

export type CloudRuntimeConnectionFailure = Extract<
  CloudRuntimeConnectionStatus,
  { readonly kind: "failed" }
>;

/**
 * Maps an actionable connection refusal without coupling the caller to the
 * short-lived progress stream. The new-thread landing needs this narrow form
 * because durable setup progress belongs to the thread launch card.
 */
export function deriveCloudRuntimeConnectionFailure({
  error,
  errorCode = null,
  failureResidual = null,
}: Pick<
  CloudRuntimeConnectionStatusInput,
  "error" | "errorCode" | "failureResidual"
>): CloudRuntimeConnectionFailure | null {
  if (isCloudRuntimeTerminalFailureCode(errorCode)) {
    const terminalCode = errorCode as string;
    return {
      kind: "failed",
      label: "Workspace could not start",
      why: terminalRuntimeWhyCopy(terminalCode, failureResidual),
      message: TERMINAL_RUNTIME_FOLLOW_UP,
      action: "open-environments",
      errorCode: terminalCode,
      residualLine: formatTerminalFailureResidualForDisplay(failureResidual),
    };
  }

  if (error === null) return null;

  const normalizedError = error.toLowerCase();
  const recoveryInProgress =
    isCloudRuntimeRecoveryCode(errorCode) ||
    normalizedError.includes("credential rotation is in progress") ||
    normalizedError.includes("replacement is still in progress");
  if (recoveryInProgress) {
    return {
      kind: "failed",
      label: "Project is still recovering",
      message: "No action is needed. Try again later.",
      action: "retry",
    };
  }

  if (
    errorCode === MISSING_FORGE_CLOUD_SERVER_ERROR_CODE ||
    normalizedError.includes("did not become ready within 5 minutes")
  ) {
    return {
      kind: "failed",
      label: "Forge cloud server needed",
      message: MISSING_FORGE_CLOUD_SERVER_MESSAGE,
      action: "setup-retry",
    };
  }

  return {
    kind: "failed",
    label: "Could not connect",
    message: "Try again after Setup finishes, or report the issue if it keeps happening.",
    action: "setup-retry",
  };
}

export function deriveCloudRuntimeConnectionStatus({
  events,
  isConnecting,
  error,
  errorCode = null,
  failureResidual = null,
}: CloudRuntimeConnectionStatusInput): CloudRuntimeConnectionStatus {
  const failure = deriveCloudRuntimeConnectionFailure({ error, errorCode, failureResidual });
  if (failure) return failure;

  const latestEvent = events.at(-1) ?? null;
  if (!isConnecting || latestEvent?.stage === "connected") return { kind: "hidden" };

  const isRetry =
    latestEvent?.stage === "connect-retry" ||
    latestEvent?.stage === "runtime-ensure-retry" ||
    latestEvent?.stage === "environment-register-retry" ||
    (latestEvent?.stage === "connect-request" && (latestEvent.attempt ?? 1) > 1);
  return isRetry
    ? { kind: "reconnecting", label: "Reconnecting..." }
    : { kind: "connecting", label: "Connecting..." };
}
